import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {axiosInstance} from '../routes/axiosInstance';
import {orderByTimestamp} from "../helpers/datetimeHelpers";
import { normalizeManager } from '../pages/admin/data/resources/resourceUtils';

// Helper function to extract validation errors from API response
const extractValidationErrors = (error) => {
  const responseData = error.response?.data || {};

  // Check multiple possible locations for validation errors
  // 1. responseData.data (wrapped errors)
  // 2. responseData itself (direct field errors like {"password": ["error"]})
  let validationErrors = responseData?.data || responseData;

  // If we have field-specific validation errors, format them
  if (validationErrors && typeof validationErrors === 'object' && Object.keys(validationErrors).length > 0) {
    // Filter out non-error fields (like 'message', 'status', etc.)
    const errorFields = Object.entries(validationErrors).filter(([key, value]) =>
      Array.isArray(value) || (typeof value === 'string' && !['message', 'status', 'detail'].includes(key))
    );

    if (errorFields.length > 0) {
      return errorFields
        .map(([field, messages]) => {
          const rawArray = Array.isArray(messages) ? messages : [messages];
          // Coerce every entry to a string. Some endpoints (e.g. POST
          // /user/payment/calculate's 422) return arrays of objects under
          // a field name; without this guard, .toLowerCase() crashes on
          // them and the crash text becomes the user-facing toast.
          const msgArray = rawArray.map(m =>
            typeof m === 'string' ? m : JSON.stringify(m)
          );
          // Capitalize field name for better UX
          const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          // Only include field name if it adds clarity, otherwise just show the message
          const needsFieldName = msgArray.some(msg => !msg.toLowerCase().includes(field.toLowerCase()));
          return needsFieldName ? `${fieldName}: ${msgArray.join(', ')}` : msgArray.join(', ');
        })
        .join('. ');
    }
  }

  // Fallback to message or error fields
  return responseData?.message || responseData?.error || responseData?.detail || null;
};

// Helper function for generic error handling
export const handleThunkError = (error, fallbackMessage) => {
  const validationError = extractValidationErrors(error);
  return validationError || fallbackMessage;
};

export const fetchPaymentRequirements = createAsyncThunk(
  'user/fetchPaymentRequirements',
  async ({year, month, resource}, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get(
        `/user/payment/validate-requirements?year=${year}&month=${month}&resource=${resource}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch payment requirements.'));
    }
  });

export const fetchPaymentRequests = createAsyncThunk(
  'user/fetchPaymentRequests',
  async ({year, month, resource}, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get(
        `/user/payment/requests?year=${year}&month=${month}&track=total&resource=${resource}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch payment requests.'));
    }
  });

export const requestUserPayments = createAsyncThunk(
  'user/requestUserPayments',
  async ({year, month, resource, group, userList, extraCc, allowResend = false}, {rejectWithValue}) => {
    try {
      const body = {
        year, month, user_list: userList, allow_resend: allowResend,
      };
      if (group) {
        body.group = group;
      } else {
        body.resource = resource;
      }
      if (extraCc && extraCc.length) {
        body.extra_cc = extraCc;
      }
      const response = await axiosInstance.post('/user/payment/send-email', body);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
        message: handleThunkError(error, error.message),
      });
    }
  });

export const fetchUserPayments = createAsyncThunk(
  'user/fetchUserPayments',
  async ({year, month, resource, horizonCategory}, {rejectWithValue}) => {
    try {
      let url = `/user/payment/list?year=${year}&month=${month}&track=total&resource=${resource}`;
      if (horizonCategory && horizonCategory !== 'all') {
        url += `&horizon_category=${horizonCategory}`;
      }
      const response = await axiosInstance.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch user payments.'));
    }
  });

export const calculatePayments = createAsyncThunk(
  'user/calculatePayments',
  async ({ resources, ignoreOutstanding = false } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/user/payment/calculate', {
        resources: resources,
        ignore_outstanding: ignoreOutstanding
      });
      return response.data.data;
    } catch (error) {
      // 422 + 207 from CalculatePaymentsView wrap the per-resource outcome
      // in {data: {success, resources_failed, details: [{resource_id, success, error}]}}.
      // Extract the first per-resource error string for a clean toast like
      // "No scores found for resource <id>" instead of the generic fallback.
      const result = error?.response?.data?.data;
      if (result && Array.isArray(result.details)) {
        const firstFailure = result.details.find((d) => d && !d.success);
        if (firstFailure?.error) {
          return rejectWithValue(String(firstFailure.error));
        }
      }
      return rejectWithValue(handleThunkError(error, 'Failed to calculate payments.'));
    }
  });

export const fetchPaymentValidations = createAsyncThunk(
  'user/fetchPaymentValidations',
  async ({year, month, resource}, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get(
        `/user/payment/validation-report?year=${year}&month=${Number(month)}&resource=${resource}`
      );
      return {
        data: response.data.data,
        forKey: `${resource}|${year}|${Number(month)}`,
      };
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to run payment validations.'));
    }
  });

// Thunk to fetch logged-in user details
export const fetchUserDetails = createAsyncThunk(
  'user/fetchUserDetails',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/user/user-detail');
      return response.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch user details.'));
    }
  }
);

export const deleteResource = createAsyncThunk(
  'data/deleteResource',
  async (resourceId, {rejectWithValue}) => {
    try {
      await axiosInstance.delete(`/user/resource/${resourceId}`);
      return resourceId;
    } catch (error) {
      console.error('Failed to delete resource:', error);
      return rejectWithValue(handleThunkError(error, 'Failed to delete resource.'));
    }
  }
);

export const addResource = createAsyncThunk(
  'data/addResource',
  async (newResourceData, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.post('/user/resource/', newResourceData);
      return response.data;
    } catch (error) {
      console.error('Failed to add resource:', error);
      return rejectWithValue(handleThunkError(error, 'Failed to add resource.'));
    }
  }
);

export const patchResource = createAsyncThunk(
  'data/patchResource',
  async ({resourceId, updates}, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.patch(`/user/resource/${resourceId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Failed to update resource:', error);
      return rejectWithValue(handleThunkError(error, 'Failed to update resource.'));
    }
  }
);

export const patchUserNotifications = createAsyncThunk(
  'user/patchUserNotifications',
  async ({updates}, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.patch('/user/notification/', updates);
      return response.data;
    } catch (error) {
      console.error('Failed to update user notifications:', error);
      return rejectWithValue(handleThunkError(error, 'Failed to update user.'));
    }
  }
);

export const patchUserDetails = createAsyncThunk(
  'user/patchUserDetails',
  async ({updates}, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.patch('/user/user-detail/', updates);
      return response.data;
    } catch (error) {
      console.error('Failed to update user details:', error);
      return rejectWithValue(handleThunkError(error, 'Failed to update user details.'));
    }
  }
);

export const fetchResources = createAsyncThunk(
  'user/resource',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/user/resource');
      return response.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch resources.'));
    }
  }
);

export const fetchResourceParticipants = createAsyncThunk(
  'user/fetchResourceParticipants',
  async (resourceId, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get(`/user/resource-participation/resource/${resourceId}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch participants.'));
    }
  }
);

export const updateParticipationType = createAsyncThunk(
  'user/updateParticipationType',
  async ({participantId, is_fixed_payment, resourceId}, {rejectWithValue}) => {
    try {
      // Include resource id in request body so backend can validate/update the participant for the given resource
      const response = await axiosInstance.put(`/user/resource-participation/${participantId}/`, {is_fixed_payment, resource: resourceId});
      return response.data.data;
    } catch (error) {
      console.error('Failed to update participation type:', error);
      return rejectWithValue(handleThunkError(error, 'Failed to update participation type.'));
    }
  }
);

export const fetchUsers = createAsyncThunk(
  'user/fetchUsers',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/user/list');
      return orderByTimestamp(response.data.data, 'expiration_time', 'desc');
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch users.'));
    }
  }
);

export const fetchInvites = createAsyncThunk(
  'user/fetchInvites',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/user/list-invite');
      return orderByTimestamp(response.data, 'expiration_time', 'desc');
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch invites.'));
    }
  }
);

export const fetchManagedResources = createAsyncThunk(
  'user/fetchManagedResources',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/user/resource-manager');
      const resourceData = response.data?.data || response.data || [];

      // Extract unique resources (user might manage same resource multiple times)
      const uniqueResourcesMap = new Map();
      resourceData.forEach(item => {
        if (!uniqueResourcesMap.has(item.resource)) {
          uniqueResourcesMap.set(item.resource, {id: item.resource, name: item.resource_name});
        }
      });

      return Array.from(uniqueResourcesMap.values());
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch managed resources.'));
    }
  }
);

export const fetchResourceManagerDetails = createAsyncThunk(
  'user/fetchResourceManagerDetails',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/user/resource-manager');
      return response.data?.data || response.data || [];
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch resource manager details.'));
    }
  }
);

export const sendInvite = createAsyncThunk(
  'user/sendInvite',
  async (payload, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.post('/user/register-invite', payload);
      return response.data.data.link;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Error sending invitation. Please try again.'));
    }
  }
);

export const fetchSessionScoreboard = createAsyncThunk(
  'user/fetchSessionScoreboard',
  async (params, {rejectWithValue}) => {
    try {
      const query = params?.marketSession ? `?market_session=${params.marketSession}` : '?latest_with_results=true';
      const response = await axiosInstance.get(`/market/leaderboard/scores${query}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch session scoreboard.'));
    }
  }
);

export const fetchUserNotifications = createAsyncThunk(
  'user/fetchUserNotifications',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/user/notification');
      return response.data;
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch user notifications.'));
    }
  }
);

export const fetchManagersForResource = createAsyncThunk(
  'user/fetchManagersForResource',
  async (resourceId, {rejectWithValue}) => {
    try {
      const res = await axiosInstance.get(`/user/resource-manager?resource=${encodeURIComponent(resourceId)}`);
      const data = res.data?.data || res.data || [];
      // normalize entries
      const normalized = (Array.isArray(data) ? data : []).map(normalizeManager);
      return {resourceId, data: normalized};
    } catch (error) {
      return rejectWithValue({resourceId, message: handleThunkError(error, 'Failed to fetch managers for resource')});
    }
  }
);

export const fetchUserActivities = createAsyncThunk(
  'user/fetchUserActivities',
  async ({
    userId,
    activityType,
    startDate,
    endDate,
    page = 1,
    pageSize = 20,
    ordering = 'desc',
    // Optional metadata sub-filter — one of: { variable, preference, export_type }.
    // Drives the stats-panel drill-down (e.g. clicking q50 inside Forecasts).
    metadataFilter,
  }, {rejectWithValue}) => {
    try {
      const params = new URLSearchParams();
      if (userId) params.append('user_id', userId);
      if (activityType && activityType !== 'all') params.append('activity_type', activityType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      params.append('page', page);
      params.append('page_size', pageSize);
      if (ordering) params.append('ordering', ordering);
      if (metadataFilter?.key && metadataFilter?.value) {
        params.append(metadataFilter.key, metadataFilter.value);
      }

      const response = await axiosInstance.get(`/user/activity/?${params.toString()}`);
      return response.data.data; // CustomRenderer wraps in { code, data }
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch user activities'));
    }
  }
);

export const fetchUserActivityStats = createAsyncThunk(
  'user/fetchUserActivityStats',
  async ({ startDate, endDate, userId }, {rejectWithValue}) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      // Scope-to-user: when the UI filters by a specific user, all stats
      // (total, breakdowns) should reflect that user only.
      if (userId) params.append('user_id', userId);

      const response = await axiosInstance.get(`/user/activity/stats/?${params.toString()}`);
      return response.data.data; // CustomRenderer wraps in { code, data }
    } catch (error) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch user activity stats'));
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState: {
    userDetails: {
      notifications: {
        invoiceEmail: '',
        invoiceContactName: '',
        invoiceContactEmail: '',
        alertsEmail: '',
        otherEmail: '',
        enableInvoiceEmails: true,
        enableAlertEmails: true,
        enableForecastSubmissionAlerts: true,
      },
    },
    selectedResource: null,
    resourcesLoaded: false,
    userPayments: [],
    paymentRequests: null,
    paymentRequirements: null,
    paymentRequestsLoading: false,
    paymentFilters: {
      year: null,
      month: null,
      metric: 'total',
      horizonCategory: 'all',
    },
    paymentValidations: {status: 'idle', data: null, error: null, forKey: null},
    resources: [],
    participants: [],
    participantsSummary: null,
    participantsLoading: false,
    // Resource the `participants` list was fetched for. Consumers that switch
    // resources need this to tell a fresh list from the previous resource's
    // rows, which stay in state until the new fetch resolves.
    participantsResourceId: null,
    usersList: [],
    invites: [], // Added for managing invites
    managedResources: [], // Resources the user owns or manages
    managedResourcesLoading: false,
    resourceManagerDetails: [], // Full resource manager details with roles
    resourceManagerDetailsLoading: false,
    // per-resource managers cache: { [resourceId]: { data: [], loading: false, error: null, lastFetched: number } }
    managersByResource: {},
    sessionScoreboard: null, // Added for managing the session scoreboard
    loading: false,
    errorMessage: '',
    successMessage: '',
    invitationLink: '',
  },
  reducers: {
    // seed managers for a resource (useful when resource already contains managers inline)
    setManagersForResource(state, action) {
      const { resourceId, data } = action.payload || {};
      if (!resourceId) return;
      state.managersByResource[resourceId] = {
        data: Array.isArray(data) ? data.map(normalizeManager) : [],
        loading: false,
        error: null,
        lastFetched: Date.now(),
      };
    },
    setPaymentFilters(state, action) {
      state.paymentFilters = {
        ...state.paymentFilters,
        ...action.payload,
      };
    },
    clearPaymentRequests(state) {
      state.paymentRequests = null;
    },
    clearPaymentValidations(state) {
      state.paymentValidations = {status: 'idle', data: null, error: null, forKey: null};
    },
    updateParticipantLocally(state, action) {
      const { participantId, is_fixed_payment } = action.payload;
      const participantIndex = state.participants.findIndex(p => p.id === participantId);

      if (participantIndex !== -1) {
        // Update the participant
        state.participants[participantIndex].is_fixed_payment = is_fixed_payment;

        // Update the summary counts
        if (state.participantsSummary) {
          if (is_fixed_payment) {
            state.participantsSummary.fixed_payment_count += 1;
            state.participantsSummary.performance_based_count -= 1;
          } else {
            state.participantsSummary.fixed_payment_count -= 1;
            state.participantsSummary.performance_based_count += 1;
          }
        }
      }
    },
    clearUserMessages(state) {
      state.errorMessage = '';
      state.successMessage = '';
    },
    clearInvitationLink(state) {
      state.invitationLink = '';
    },
    // Set a single notification field { key, value }
    setNotificationField(state, action) {
      const { key, value } = action.payload || {};
      state.userDetails.notifications = state.userDetails.notifications || {};
      if (key) state.userDetails.notifications[key] = value;
    },
  },
  extraReducers: (builder) => {
    builder
      // Managers per resource thunk
      .addCase(fetchManagersForResource.pending, (state, action) => {
        const id = action.meta.arg;
        state.managersByResource[id] = state.managersByResource[id] || {};
        state.managersByResource[id].loading = true;
        state.managersByResource[id].error = null;
      })
      .addCase(fetchManagersForResource.fulfilled, (state, action) => {
        const { resourceId, data } = action.payload || {};
        state.managersByResource[resourceId] = {
          data: data || [],
          loading: false,
          error: null,
          lastFetched: Date.now(),
        };
      })
      .addCase(fetchManagersForResource.rejected, (state, action) => {
        const id = action.payload?.resourceId || action.meta.arg;
        state.managersByResource[id] = state.managersByResource[id] || {};
        state.managersByResource[id].loading = false;
        state.managersByResource[id].error = action.payload?.message || action.error?.message || 'Failed to fetch managers';
      })
      // User Details
      .addCase(fetchUserDetails.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = {...(state.userDetails || {}), ...(action.payload || {})};
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      // Resources
      .addCase(fetchResources.fulfilled, (state, action) => {
        state.loading = false;
        state.resourcesLoaded = true;
        state.resources = action.payload.data || [];
      })
      .addCase(patchResource.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      // Resource Participants
      .addCase(fetchResourceParticipants.pending, (state) => {
        state.participantsLoading = true;
        state.participantsResourceId = null;
        state.errorMessage = '';
      })
      .addCase(fetchResourceParticipants.fulfilled, (state, action) => {
        state.participantsLoading = false;
        state.participantsResourceId = action.meta.arg ?? null;
        const participants = action.payload.participants || [];
        state.participants = participants.sort((a, b) =>
          (a.user_email || '').toLowerCase().localeCompare((b.user_email || '').toLowerCase())
        );
        state.participantsSummary = {
          total_participants: action.payload.total_participants || 0,
          fixed_payment_count: action.payload.fixed_payment_count || 0,
          performance_based_count: action.payload.performance_based_count || 0,
        };
      })
      .addCase(fetchResourceParticipants.rejected, (state, action) => {
        state.participantsLoading = false;
        state.participantsResourceId = null;
        state.participants = [];
        state.participantsSummary = null;
        state.errorMessage = action.payload;
      })
      .addCase(updateParticipationType.pending, (state) => { state.loading = true; })
      .addCase(updateParticipationType.fulfilled, (state) => { state.loading = false; })
      .addCase(updateParticipationType.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      // Users & Invites
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.usersList = action.payload || [];
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      .addCase(fetchInvites.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchInvites.fulfilled, (state, action) => {
        state.loading = false;
        state.invites = action.payload;
      })
      .addCase(fetchInvites.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      .addCase(sendInvite.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
        state.successMessage = '';
        state.invitationLink = '';
      })
      .addCase(sendInvite.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Invitation sent successfully.';
        state.invitationLink = action.payload;
      })
      .addCase(sendInvite.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      // Managed Resources
      .addCase(fetchManagedResources.pending, (state) => {
        state.managedResourcesLoading = true;
        state.errorMessage = '';
      })
      .addCase(fetchManagedResources.fulfilled, (state, action) => {
        state.managedResourcesLoading = false;
        state.managedResources = action.payload;
      })
      .addCase(fetchManagedResources.rejected, (state) => {
        state.managedResourcesLoading = false;
        state.managedResources = [];
      })
      // Resource Manager Details
      .addCase(fetchResourceManagerDetails.pending, (state) => {
        state.resourceManagerDetailsLoading = true;
        state.errorMessage = '';
      })
      .addCase(fetchResourceManagerDetails.fulfilled, (state, action) => {
        state.resourceManagerDetailsLoading = false;
        state.resourceManagerDetails = action.payload;
      })
      .addCase(fetchResourceManagerDetails.rejected, (state) => {
        state.resourceManagerDetailsLoading = false;
        state.resourceManagerDetails = [];
      })
      // Session Scoreboard
      .addCase(fetchSessionScoreboard.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchSessionScoreboard.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionScoreboard = action.payload;
      })
      .addCase(fetchSessionScoreboard.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      // Payments
      .addCase(fetchUserPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.userPayments = [...action.payload].sort((a, b) => b.month_payment - a.month_payment);
      })
      .addCase(fetchUserPayments.rejected, (state, action) => {
        state.loading = false;
        state.userPayments = [];
        state.errorMessage = action.payload;
      })
      .addCase(fetchPaymentRequests.pending, (state) => { state.paymentRequestsLoading = true; })
      .addCase(fetchPaymentRequests.fulfilled, (state, action) => {
        state.paymentRequestsLoading = false;
        const results = action.payload.results;
        state.paymentRequests = results.length > 0 ? results : null;
      })
      .addCase(fetchPaymentRequests.rejected, (state) => {
        state.paymentRequestsLoading = false;
        state.paymentRequests = null;
      })
      .addCase(fetchPaymentRequirements.fulfilled, (state, action) => {
        state.paymentRequirements = action.payload;
      })
      .addCase(fetchPaymentValidations.pending, (state) => {
        state.paymentValidations.status = 'loading';
        state.paymentValidations.error = null;
      })
      .addCase(fetchPaymentValidations.fulfilled, (state, action) => {
        state.paymentValidations = {
          status: 'succeeded',
          data: action.payload.data,
          error: null,
          forKey: action.payload.forKey,
        };
      })
      .addCase(fetchPaymentValidations.rejected, (state, action) => {
        state.paymentValidations.status = 'failed';
        state.paymentValidations.error = action.payload || 'Validation request failed.';
        state.paymentValidations.forKey = action.meta.arg
          ? `${action.meta.arg.resource}|${action.meta.arg.year}|${Number(action.meta.arg.month)}`
          : null;
      })
      // User Notifications & Details Updates
      .addCase(patchUserNotifications.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(patchUserNotifications.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'User updated successfully.';
      })
      .addCase(patchUserNotifications.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const notificationsData = payload.data ?? payload;
        state.userDetails = state.userDetails || {};
        state.userDetails.notifications = notificationsData;
      })
      .addCase(fetchUserNotifications.rejected, (state, action) => {
        state.errorMessage = action.payload;
      })
      .addCase(patchUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = {...(state.userDetails || {}), ...(action.payload || {})};
        state.successMessage = 'User details updated successfully.';
      });
  },
});

export const {
  clearUserMessages,
  clearInvitationLink,
  setPaymentFilters,
  clearPaymentRequests,
  clearPaymentValidations,
  setNotificationField,
  updateParticipantLocally,
  setManagersForResource
} = userSlice.actions;
export default userSlice.reducer;

// A market owner account, as the backend defines one: not a forecaster and
// not a platform admin. Same rule as PaymentGroupSerializer.validate and
// UserResourcesUpdateSerializer, so any page offering owner choices narrows
// its options the way the API will accept them.
export const isMarketOwnerAccount = (user) => !!user
  && user.is_superuser !== true && user.is_forecaster === false;

// Selectors
export const selectUserDetails = (state) => state.user.userDetails;
export const selectUsersList = (state) => state.user.usersList;
export const selectInvites = (state) => state.user.invites; // Selector for invites
export const selectManagedResources = (state) => state.user.managedResources; // Selector for managed resources
export const selectManagedResourcesLoading = (state) => state.user.managedResourcesLoading;
export const selectResourceManagerDetails = (state) => state.user.resourceManagerDetails; // Selector for resource manager details with roles
export const selectResourceManagerDetailsLoading = (state) => state.user.resourceManagerDetailsLoading;
export const selectUserLoading = (state) => state.user.loading;
export const selectUserError = (state) => state.user.errorMessage;
export const selectUserSuccess = (state) => state.user.successMessage;
export const selectInvitationLink = (state) => state.user.invitationLink;
export const selectResources = (state) => state.user.resources;
export const selectSessionScoreboard = (state) => state.user.sessionScoreboard;
export const selectErrorMessage = (state) => state.user.errorMessage;
export const selectResourcesLoaded = (state) => state.user.resourcesLoaded;
export const selectUserPayments = (state) => state.user.userPayments;
export const selectPaymentFilters = (state) => state.user.paymentFilters;
export const selectPaymentRequests = (state) => state.user.paymentRequests;
export const selectPaymentRequestsLoading = (state) => state.user.paymentRequestsLoading;
export const selectPaymentRequirements = (state) => state.user.paymentRequirements;
export const selectPaymentValidations = (state) => state.user.paymentValidations;
export const selectParticipants = (state) => state.user.participants;
export const selectParticipantsLoading = (state) => state.user.participantsLoading;
export const selectParticipantsResourceId = (state) => state.user.participantsResourceId;
export const selectParticipantsSummary = (state) => state.user.participantsSummary;
// Managers by resource selector
export const selectManagersByResource = (state, resourceId) => {
  return state.user.managersByResource[resourceId]?.data || [];
};
// Manager loading state by resource selector
export const selectManagersLoadingByResource = (state, resourceId) => {
  return state.user.managersByResource[resourceId]?.loading || false;
};

// Manager error state by resource selector
export const selectManagersErrorByResource = (state, resourceId) => {
  return state.user.managersByResource[resourceId]?.error || null;
};
